import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Box, Button, Dialog, DialogContent, Stack } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import Api from './API';
import { styles } from './styles';
import Search from '../SmartForms/Search';
import GenericTable from '../Components/GenericTable/GenericTable';
import { CandidatesReducer, InitialCandidatesState } from './reducer';
import {
  FilterKey,
  ICandidateGroup,
  ICandidateGroupsApiResponse,
  ICandidateSourcingFilters,
  ICandidateSourcingSearch,
  IJob
} from './types';
import TotalSelectedIndicatorWithActions from '../Components/GenericTable/TotalSelectedIndicatorWithActions';
import DeleteIcon from '@mui/icons-material/Delete';
import { tableSearchButtonStyles, toggleScreenButtonStyles } from '../shared/commonStyles';
import JobSelectionDialog from './JobSelectionDialog';
import FelixAnimation from './FelixAnimation';
import Filters from './Filters';
import { EditFiltersModal } from './EditFiltersModal';
import CandidateGroupTableRows from './CandidateGroupTableRows';
import { headerStyle } from '../Candidates/config';
import { FelixGPTIcon } from '../../AIStudio/SharedComponents/Graphics/FelixSVG';
import EditableSearchName from './EditableSearchName';
import AddToCandidatePool from '../CandidateSourcing/AddToCandidatePool';
import { PlaylistAdd } from '@mui/icons-material';
import WithPoolModal from '../CandidateSourcing/WithPoolModal';
import { useCandidatePoolModal } from '../CandidateSourcing/hooks/useCandidatePoolModal';
import ConditionsDialog from '../AgenticScreening/ConditionsDialog';
import { Condition } from '../CandidateSourcing/types';
import CandidateGroupPopover from './CandidateGroupPopover';
import CandidateGroup from '../CandidateGroup/CandidateGroup';
import { useDebouncedCallback } from 'use-debounce';
import { useWebSockets } from '../Components/Utilities/useWebSockets';
import { ICableData } from '../AgenticScreening/types';

export default function AgenticMatching({ searchObj }: { searchObj: ICandidateSourcingSearch }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [CandidatesState, dispatch] = useReducer(CandidatesReducer, InitialCandidatesState);
  const [expandedPools, setExpandedPools] = useState<Set<number>>(new Set());
  const [candidateHovered, setCandidateHovered] = useState(false);
  const [lastHoveredCandidateId, setLastHoveredCandidateId] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isCandidateGroupModalOpen, setIsCandidateGroupModalOpen] = useState(false);
  const [selectedCandidateGroup, setSelectedCandidateGroup] = useState<ICandidateGroup | null>(
    null
  );
  const {
    candidatesCount,
    candidatesTableSettings,
    candidatesTableState,
    optionsCleared,
    isJobDialogOpen,
    isAddToCandidatePoolOpen,
    typesToExpand,
    totalPages,
    currentStep,
    transitioningStep,
    progressVisible,
    showResults
  } = CandidatesState;
  const selectedItemIds = candidatesTableState.selectedItemIds;
  const [searchObject, setSearchObject] = useState(searchObj);
  const [selectedJob, setSelectedJob] = useState<IJob | undefined>(searchObject.job);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isConditionsDialogOpen, setIsConditionsDialogOpen] = useState(false);
  const [currentConditions, setCurrentConditions] = useState<Condition[]>([]);
  const screenedCandidatesCount = searchObject.screened_candidates_count || 0;

  const { subscribeToChannel } = useWebSockets('CandidateSourcingChannel', (data: ICableData) => {
    if (data.kind === 'contacts_revealed') {
      refetchCandidates();
    }
  });

  const candidatePoolModalHook = useCandidatePoolModal();
  const { handlePoolClick } = candidatePoolModalHook;

  const handleFiltersChange = async (newFilters: ICandidateSourcingFilters) => {
    setSearchObject({
      ...searchObject,
      filters: newFilters
    });
    updateFilters(newFilters);
  };

  const onScreenCandidatesClick = () => {
    refetchConditions();
    setIsConditionsDialogOpen(true);
  };

  const filters = (searchObject.filters || {}) as ICandidateSourcingFilters;

  const transitionToStep = (step: string) => {
    dispatch({ type: 'SET_TRANSITIONING_STEP', payload: true });
    setTimeout(() => {
      dispatch({ type: 'SET_CURRENT_STEP', payload: step });
    }, 400);
    setTimeout(() => {
      dispatch({ type: 'SET_TRANSITIONING_STEP', payload: false });
    }, 600);
  };

  const setErrorSnackbar = (message: string) => {
    dispatch({
      type: 'SET_SNACKBAR',
      payload: { message, state: 'error' }
    });
  };

  const { mutate: updateSearch } = useMutation({
    mutationFn: async ({ query, jobId }: { query: string; jobId?: number }) => {
      const response = await Api.updateSearch(searchObj.id, query, jobId);
      setSearchObject(response.res as ICandidateSourcingSearch);
      return response;
    }
  });

  const { mutate: updateSearchName, isLoading: isUpdatingName } = useMutation({
    mutationFn: async (name: string) => {
      const response = await Api.updateSearchName(searchObj.id, name);
      setSearchObject(response.res as ICandidateSourcingSearch);
      return response;
    },
    onSuccess: () => {
      dispatch({
        type: 'SET_SNACKBAR',
        payload: { message: 'Search name updated successfully.', state: 'success' }
      });
    },
    onError() {
      setErrorSnackbar('There was an error updating the search name.');
    }
  });

  const { mutate: executeSearch, isLoading: isExecutingSearch } = useMutation({
    mutationFn: async () => {
      transitionToStep('Executing search...');
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.executeSearch(searchObj.id, false);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
      dispatch({ type: 'COMPLETE_EXECUTING_SEARCH' });
      transitionToStep('Preparing screening conditions...');
      setTimeout(() => {
        transitionToStep('');
        dispatch({ type: 'COMPLETE_PROGRESS' });
      }, 10000);
    },
    onError() {
      setErrorSnackbar('There was an error executing the search.');
    }
  });

  const { mutate: generateConditions } = useMutation({
    mutationFn: async () => {
      const response = await Api.generateConditions(searchObj.id);
      return response;
    }
  });

  const { mutate: updateFilters, isLoading: isUpdatingFilters } = useMutation({
    mutationFn: async (newFilters: ICandidateSourcingFilters) => {
      dispatch({ type: 'SET_PAGE', payload: 1 });
      const response = await Api.updateFilters(searchObj.id, newFilters);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error updating the filters.');
    }
  });

  const { mutate: expandFilters } = useMutation({
    mutationFn: async () => {
      const firstType = typesToExpand[0];
      let label = '';
      let extension = '';
      switch (firstType) {
        case 'job_titles':
          label = 'job titles';
          if (filters.job_titles && filters.job_titles.length === 1) {
            extension = ` to "${filters.job_titles[0]}"`;
          }
          break;
        case 'skills':
          label = 'skills';
          if (filters.skills && filters.skills[0] && filters.skills[0].length === 1) {
            extension = ` to "${filters.skills[0][0]}"`;
          }
          break;
        case 'industries':
          label = 'industries';
          if (filters.industries && filters.industries.length === 1) {
            extension = ` to "${filters.industries[0]}"`;
          }
          break;
      }
      transitionToStep(`Looking up similar ${label}${extension}...`);
      const response = await Api.expandFilters(searchObj.id, firstType, false);
      if (response.res) {
        setSearchObject(response.res as ICandidateSourcingSearch);
      }
      return response;
    },
    onSuccess: () => {
      const firstType = typesToExpand[0];
      const remainingTypes = typesToExpand.slice(1);
      dispatch({ type: 'SET_TYPES_TO_EXPAND', payload: remainingTypes });
      expandFiltersWithOptions(firstType);
      if (remainingTypes.length === 0) {
        executeSearch();
      }
    },
    onError() {
      setErrorSnackbar('There was an error expanding the filters.');
    }
  });

  const { mutate: expandFiltersWithOptions } = useMutation({
    mutationFn: async (type: string) => {
      const response = await Api.expandFilters(searchObj.id, type, true);
      if (response.res) {
        setSearchObject(response.res as ICandidateSourcingSearch);
      }
      return response;
    }
  });

  const { mutate: removeSelectedCandidateGroups } = useMutation({
    mutationFn: async (ids: number[]) => {
      const response = await Api.bulkRemoveCandidates(searchObj.id, ids);
      return response;
    },
    onSuccess: () => {
      refetchCandidates();
    },
    onError() {
      setErrorSnackbar('There was an error deleting the selected candidates.');
    }
  });

  const {
    data: candidateGroupsData,
    isLoading: isFetchingCandidates,
    refetch: refetchCandidates
  } = useQuery({
    queryKey: [
      'candidate_groups',
      searchObj.id,
      candidatesTableSettings.page,
      candidatesTableSettings.limit,
      candidatesTableSettings.sort,
      candidatesTableSettings.sort_order
    ],
    queryFn: async () => {
      const candidateGroupsResponse = (await Api.fetchCandidateGroups(
        searchObj.id,
        candidatesTableSettings.page,
        candidatesTableSettings.limit,
        candidatesTableSettings.sort,
        candidatesTableSettings.sort_order
      )) as ICandidateGroupsApiResponse;
      dispatch({
        type: 'SET_CANDIDATES_COUNT',
        payload: parseInt(candidateGroupsResponse.resHead?.['x-total-count'] || '0')
      });
      dispatch({
        type: 'SET_TOTAL_PAGES',
        payload: parseInt(candidateGroupsResponse.resHead?.['x-total-pages'] || '0')
      });
      return candidateGroupsResponse;
    }
  });

  const { refetch: refetchConditions } = useQuery({
    queryKey: ['conditions', searchObj.id],
    queryFn: async () => {
      const response = await Api.getConditions(searchObj.id);
      const conditions = (response.res as { conditions: Condition[] }).conditions;
      setCurrentConditions(conditions);
      return response;
    }
  });

  const candidateGroups = useMemo(
    () => candidateGroupsData?.res?.candidate_groups || [],
    [candidateGroupsData]
  );

  const blankStep =
    candidateGroups.length > 0
      ? 'Select some candidates to screen and rank them'
      : 'Enter the search criteria and start searching for your ideal candidates';
  const displayedCurrentStep = currentStep || blankStep;

  const setQuery = useDebouncedCallback((newQuery: string) => {
    updateSearch({
      query: newQuery,
      jobId: selectedJob?.id
    });
    dispatch({ type: 'SET_OPTIONS_CLEARED', payload: true });
  }, 600);

  const doSearch = async () => {
    const typesToExpand = ['job_titles', 'skills', 'industries'].filter(
      (filter) =>
        filters[filter as FilterKey] && (filters[filter as FilterKey] as string[]).length > 0
    );
    setSearchObject({ ...searchObject, executed_at: new Date().toISOString() });
    dispatch({ type: 'START_SEARCH', payload: { typesToExpand } });
    await generateConditions();
  };

  const handleJobSelect = (job: IJob | undefined) => {
    setSelectedJob(job);
    updateSearch({
      query: searchObject.query,
      jobId: job?.id
    });
  };

  const handleSelectAll = async () => {
    const response = await Api.allCandidateGroupIds(searchObj.id);
    const ids = (response.res as { ids: number[] }).ids;
    dispatch({ type: 'SET_SELECTED_ITEM_IDS', payload: ids });
  };

  const handleTogglePoolsExpansion = useCallback((candidateGroupId: number) => {
    setExpandedPools((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(candidateGroupId)) {
        newSet.delete(candidateGroupId);
      } else {
        newSet.add(candidateGroupId);
      }
      return newSet;
    });
  }, []);

  const handleHoverCandidate = useCallback((target: HTMLElement | null, candidateId?: number) => {
    if (candidateId !== undefined) {
      setLastHoveredCandidateId(candidateId);
    }
    setAnchorEl(target);
    setCandidateHovered(true);
  }, []);

  const handleUnhoverCandidate = useCallback(() => {
    setCandidateHovered(false);
  }, []);

  const handleOpenCandidateGroup = useCallback((group: ICandidateGroup) => {
    setSelectedCandidateGroup(group);
    setIsCandidateGroupModalOpen(true);
  }, []);

  const handleCloseCandidateGroup = useCallback(() => {
    setIsCandidateGroupModalOpen(false);
    setSelectedCandidateGroup(null);
  }, []);

  const currentGroupIndex = useMemo(() => {
    if (!selectedCandidateGroup) return -1;
    return candidateGroups.findIndex((group) => group.id === selectedCandidateGroup.id);
  }, [selectedCandidateGroup, candidateGroups]);

  const handleNavigateCandidateGroup = useCallback(
    (index: number) => {
      if (index >= 0 && index < candidateGroups.length) {
        setSelectedCandidateGroup(candidateGroups[index]);
      }
    },
    [candidateGroups]
  );

  const allItemsInPageSelected = useMemo(() => {
    return candidateGroups.every((group) => selectedItemIds.includes(group.id));
  }, [selectedItemIds, candidateGroups]);

  const MemoizedTableRows = useCallback(
    (props: { column: unknown; item: ICandidateGroup }) => (
      <CandidateGroupTableRows
        column={props.column}
        item={props.item}
        onViewPool={handlePoolClick}
        expandedPools={expandedPools}
        onTogglePoolsExpansion={handleTogglePoolsExpansion}
        onHoverCandidate={handleHoverCandidate}
        onUnhoverCandidate={handleUnhoverCandidate}
        onOpenCandidateGroup={handleOpenCandidateGroup}
      />
    ),
    [
      expandedPools,
      handlePoolClick,
      handleTogglePoolsExpansion,
      handleHoverCandidate,
      handleUnhoverCandidate,
      handleOpenCandidateGroup
    ]
  );

  const isSearchDisabled =
    isExecutingSearch || isUpdatingFilters || isFetchingCandidates || progressVisible;

  const startScreening = async () => {
    await Api.bulkUpdateConditions(searchObj.id, currentConditions);
    await Api.startScreening(searchObj.id, selectedItemIds);
    setTimeout(() => {
      location.href = `/admin/candidate_sourcing/searches/${searchObj.id}/screening`;
    }, 1000);
  };

  useEffect(() => {
    if (typesToExpand.length > 0) {
      expandFilters();
    }
  }, [typesToExpand, expandFilters]);

  useEffect(() => {
    if (!selectedCandidateGroup) {
      return;
    }
    const updatedGroup = candidateGroups.find((group) => group.id === selectedCandidateGroup.id);
    if (updatedGroup && updatedGroup !== selectedCandidateGroup) {
      setSelectedCandidateGroup(updatedGroup);
    }
  }, [candidateGroups, selectedCandidateGroup]);

  useEffect(() => {
    subscribeToChannel();
  }, [subscribeToChannel]);

  return (
    <Stack sx={styles.candidatesPageContainer} ref={containerRef}>
      <WithPoolModal candidatePoolModalHook={candidatePoolModalHook} refetch={refetchCandidates}>
        <Box
          sx={{
            height: searchObject.name ? '42px' : '300px',
            overflow: 'hidden',
            transition: 'height 1s ease-in-out',
            position: 'relative'
          }}
        >
          <Stack
            direction="column"
            spacing={2}
            justifyContent="center"
            alignItems="center"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: searchObject.name ? 0 : 1,
              transition: 'opacity 0.5s ease-in-out'
            }}
          >
            <FelixGPTIcon />
            <h1 style={{ padding: 0, margin: 0, fontSize: '35px' }}>
              <span style={{ color: '#4E4EC7', letterSpacing: '3px' }}>:ENGAGE</span>
              &nbsp;
              <span style={{ color: '#777777' }}>by</span>
              &nbsp;&nbsp;
              <span style={{ color: '#1976D2', letterSpacing: '2px' }}>SCOUT Talent</span>
            </h1>
            <p style={{ color: '#888888', margin: 0, fontSize: '20px' }}>
              Find and connect to your ideal candidates
            </p>
          </Stack>
          <Stack
            direction="column"
            spacing={2}
            justifyContent="center"
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: searchObject.name ? 1 : 0,
              transition: 'opacity 0.5s ease-in-out'
            }}
          >
            <Stack sx={styles.candidatesPageHeader}>
              <EditableSearchName
                name={searchObject.name || ''}
                onSave={updateSearchName}
                isUpdating={isUpdatingName}
              />
            </Stack>
          </Stack>
        </Box>
        <Stack direction="row" spacing={2}>
          <Search
            idLabel="candidates"
            placeholder="Search criteria..."
            setSearch={(newQuery) => setQuery(newQuery)}
            initialValue={searchObject.query || ''}
            onEnter={() => {
              if (!isSearchDisabled) {
                doSearch();
              }
            }}
          />
          <Button
            onClick={doSearch}
            sx={{ ...tableSearchButtonStyles, marginTop: '-5px !important', padding: '0 20px' }}
            disabled={isSearchDisabled}
          >
            Search
          </Button>
        </Stack>
        <Filters
          searchObject={searchObject}
          isUpdatingFilters={isUpdatingFilters}
          onJobDialogOpen={() => dispatch({ type: 'SET_JOB_DIALOG_OPEN', payload: true })}
          onOpenEditModal={() => setIsEditModalOpen(true)}
        />
        <Stack direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
          <FelixAnimation
            transitioningStep={transitioningStep}
            progressVisible={progressVisible}
            currentStep={displayedCurrentStep}
          />
          <Stack direction="row" spacing={2}>
            <Button
              onClick={onScreenCandidatesClick}
              disabled={progressVisible || selectedItemIds.length === 0}
              sx={{
                ...tableSearchButtonStyles,
                width: '160px',
                height: '60px',
                fontSize: '16px'
              }}
            >
              Screen Candidates
            </Button>
            {screenedCandidatesCount > 0 && (
              <Button
                onClick={() => {
                  location.href = `/admin/candidate_sourcing/searches/${searchObj.id}/screening`;
                }}
                sx={{ ...toggleScreenButtonStyles, width: '146px', height: '60px' }}
              >
                View screening
              </Button>
            )}
          </Stack>
        </Stack>
        {showResults && (
          <Stack direction="row" spacing={2} sx={{ padding: '10px 0' }}>
            <em>
              Found <strong className="px-2">{candidatesCount}</strong> candidates
            </em>
          </Stack>
        )}
        <GenericTable
          label="candidates list"
          items={candidateGroups as ICandidateGroup[]}
          isLoadingItems={isFetchingCandidates || isExecutingSearch}
          tableState={candidatesTableState}
          dispatch={dispatch}
          headerStyle={headerStyle}
          paginationState={candidatesTableSettings}
          pagination={{
            totalItems: candidatesCount,
            totalPages
          }}
          TableRows={MemoizedTableRows}
          TableActionList={undefined}
          allowMultipleSelection={candidatesCount > 0}
          titleCellId="id"
          userPreferenceSource="candidate_groups"
        />
        <TotalSelectedIndicatorWithActions
          totalSelected={selectedItemIds.length}
          parentDiv={containerRef.current}
          dispatch={dispatch}
          onSelectAll={handleSelectAll}
          canSelectAll={allItemsInPageSelected && selectedItemIds.length < candidatesCount}
          totalItems={candidatesCount}
          clearAllEnabled={selectedItemIds.length > 0}
          actionButtonId="delete-candidate-action-button"
          actionButtonsList={[
            {
              id: 'add-to-pool',
              label: 'Add to Pool',
              icon: <PlaylistAdd />,
              styles: {
                color: '#084D6D',
                background: '#DEF3F2'
              },
              callback: () => {
                dispatch({ type: 'OPEN_ADD_TO_POOL_DIALOG' });
              }
            },
            {
              id: 'delete',
              label: 'Remove from Search',
              icon: <DeleteIcon />,
              callback: () => {
                removeSelectedCandidateGroups(selectedItemIds);
                dispatch({
                  type: 'REMOVE_ALL_PAGE_ITEMS',
                  payload: selectedItemIds
                });
              }
            }
          ]}
        />
        <JobSelectionDialog
          selectedJob={selectedJob}
          setSelectedJob={setSelectedJob}
          isOpen={isJobDialogOpen}
          onClose={() => dispatch({ type: 'SET_JOB_DIALOG_OPEN', payload: false })}
          onUpdate={handleJobSelect}
        />
        <AddToCandidatePool
          isOpen={isAddToCandidatePoolOpen}
          handleClose={() => dispatch({ type: 'CLOSE_ADD_TO_POOL_DIALOG' })}
          selectedCandidateGroups={selectedItemIds
            .map((id) => candidateGroups.find((group) => group.id === id))
            .filter((group): group is ICandidateGroup => group !== undefined)}
          onRemoveCandidateGroup={(candidateGroupId) => {
            dispatch({
              type: 'REMOVE_SELECTED_ITEM',
              payload: candidateGroupId
            });
          }}
          refetch={refetchCandidates}
        />
      </WithPoolModal>
      <EditFiltersModal
        filterOptions={optionsCleared ? {} : searchObject.filter_options || {}}
        filters={filters}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onFiltersChange={handleFiltersChange}
      />
      <ConditionsDialog
        isOpen={isConditionsDialogOpen}
        handleClose={() => setIsConditionsDialogOpen(false)}
        conditions={currentConditions}
        onConditionsChange={setCurrentConditions}
        onSubmit={startScreening}
        onResetConditions={() => undefined}
        submitButtonText="Screen candidates"
      />
      <CandidateGroupPopover
        candidateHovered={candidateHovered}
        handleHoverCandidate={handleHoverCandidate}
        handleUnhoverCandidate={handleUnhoverCandidate}
        lastHoveredCandidateId={lastHoveredCandidateId}
        anchorEl={anchorEl}
        searchId={searchObj.id}
      />
      <Dialog
        open={isCandidateGroupModalOpen && !!selectedCandidateGroup}
        onClose={handleCloseCandidateGroup}
        fullWidth
        maxWidth="lg"
        scroll="body"
      >
        {selectedCandidateGroup && (
          <>
            <DialogContent dividers sx={{ p: 0, overflowY: 'hidden' }}>
              <CandidateGroup
                group={selectedCandidateGroup}
                refetchCandidates={refetchCandidates}
                allGroups={candidateGroups}
                currentIndex={currentGroupIndex}
                onNavigate={handleNavigateCandidateGroup}
              />
            </DialogContent>
          </>
        )}
      </Dialog>
    </Stack>
  );
}
